import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { catchError, of, tap } from 'rxjs';
import {
  QaiCallInsightsCardComponent,
  QaiProgressBarComponent,
  QaiSegmentedBarGraphComponent,
  QaiSegmentedBarGraphKind,
} from '@qai/ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IMessageAdherenceV2ThemeScore } from 'src/app/interfaces/reportingV2/iaverage-message-adherence-v2-scores-response';
import { IMessageAdherenceAverageScoresRequest } from 'src/app/interfaces/reportingV2/imessage-adherence-average-scores-request';
import { IMessageAdherenceAverageScoresResponse } from 'src/app/interfaces/reportingV2/imessage-adherence-average-scores-response';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { getKindByPercentage } from 'src/app/utils/score-kind.util';
import { LocaleService } from 'src/app/services/locale.service';
import { SimulationDetailListModalComponent } from 'src/app/pages/reporting-dashboard/components/modals/simulation-detail-list-modal/simulation-detail-list-modal.component';

@Component({
  selector: 'app-messaging-averages',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiCallInsightsCardComponent,
    QaiProgressBarComponent,
    QaiSegmentedBarGraphComponent,
    SimulationDetailListModalComponent,
  ],
  templateUrl: './messaging-averages.component.html',
  styleUrls: ['./messaging-averages.component.scss'],
})
export class MessagingAveragesComponent {
  readonly moduleAssignmentUuid = input<string>('');
  readonly simulationUuid = input<string>('');
  readonly accountUuid = input<string>('');
  readonly teamUuids = input<string[]>([]);
  readonly promptType = input<string[]>([]);
  readonly noMetricsMessage = this.translocoService.translate(
    'NO_METRICS_MESSAGE',
    {},
    'reportingV2',
  );

  @ViewChild('listModal') private listModalRef!: SimulationDetailListModalComponent;

  // V2 data
  metrics = signal<IMessageAdherenceV2ThemeScore[]>([]);

  // V1 data
  messageAdherenceData = signal<IMessageAdherenceAverageScoresResponse[]>([]);

  readonly modalSubheading = computed(() => {
    const count = this.isV2 ? this.metrics().length : this.messageAdherenceData().length;
    return this.translocoService.translate('MODAL_SUBHEADING', { count }, 'reportingV2');
  });

  readonly objectiveFooterLabel = computed(() => {
    const count = this.isV2 ? this.metrics().length : this.messageAdherenceData().length;
    return count > 5
      ? this.translocoService.translate('VIEW_ALL_OBJECTIVES', { count }, 'reportingV2')
      : undefined;
  });

  constructor(
    private readonly reportingDashboardService: ReportingDashboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly destroyRef: DestroyRef,
    readonly localeService: LocaleService,
  ) {
    effect(() => {
      this.loadData();
    });
  }

  get isV2(): boolean {
    return this.promptType().includes(
      SimulationConstants.MESSAGING_PROMPT_TYPE_V2,
    );
  }

  get isV1(): boolean {
    return this.promptType().includes(
      SimulationConstants.MESSAGING_PROMPT_TYPE,
    );
  }

  get shouldShow(): boolean {
    return this.isV2 || this.isV1;
  }

  get heading(): string {
    return this.translocoService.translate('MESSAGING', {}, 'reportingV2');
  }

  get description(): { content: string } | string {
    if (this.isV2) {
      return {
        content: this.translocoService.translate(
          'MESSAGING_V2_DASHBOARD_DESCRIPTION',
          {},
          'reportingV2',
        ),
      };
    } else {
      return this.translocoService.translate(
        'MESSAGING_SCORE_STATS_DESCRIPTION',
        {},
        'reportingV2',
      );
    }
  }

  get v1Legend(): Array<{
    label: string;
    kind: 'success' | 'info' | 'warning';
  }> {
    return [
      {
        label: this.translocoService.translate(
          'FULLY_ARTICULATED',
          {},
          'reportingV2',
        ),
        kind: 'success',
      },
      {
        label: this.translocoService.translate(
          'PARTIALLY_ARTICULATED',
          {},
          'reportingV2',
        ),
        kind: 'info',
      },
      {
        label: this.translocoService.translate(
          'NOT_MENTIONED',
          {},
          'reportingV2',
        ),
        kind: 'warning',
      },
    ];
  }

  private loadData(): void {
    // Only load data if either v1 or v2 is enabled
    if (!this.shouldShow) {
      this.metrics.set([]);
      this.messageAdherenceData.set([]);
      this.cdr.markForCheck();
      return;
    }

    const accountUuid = this.accountUuid();
    const moduleAssignmentUuid = this.moduleAssignmentUuid();
    const simulationUuid = this.simulationUuid();
    const teamUuids = this.teamUuids() ?? [];

    if (!accountUuid || !moduleAssignmentUuid || !simulationUuid) {
      this.metrics.set([]);
      this.messageAdherenceData.set([]);
      console.log(
        'Invalid accountUuid, moduleAssignmentUuid, or simulationUuid',
      );
      return;
    }

    if (this.isV2) {
      // Load V2 data
      this.reportingDashboardService
        .getAverageMessageAdherenceV2Scores({
          accountUuid,
          moduleAssignmentUuid,
          simulationUuid,
          teamUuids,
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((items) => {
            this.metrics.set(items);
            this.cdr.markForCheck();
          }),
          catchError((err) => {
            console.error('MessagingAveragesComponent: V2 load failed', err);
            this.metrics.set([]);
            this.cdr.markForCheck();
            return of([]);
          }),
        )
        .subscribe();
    } else if (this.isV1) {
      // Load V1 data
      const messageAdherencePayload: IMessageAdherenceAverageScoresRequest = {
        moduleAssignmentUuid,
        simulationUuid,
        accountUuid,
        teamUuids,
      };
      this.reportingDashboardService
        .getMessageAdherenceAverageScores(messageAdherencePayload)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((items) => {
            this.messageAdherenceData.set(items);
            this.cdr.markForCheck();
          }),
          catchError((err) => {
            console.error('MessagingAveragesComponent: V1 load failed', err);
            this.messageAdherenceData.set([]);
            this.cdr.markForCheck();
            return of([]);
          }),
        )
        .subscribe();
    }
  }

  readonly displayedMetrics = computed(() =>
    [...this.metrics()]
      .sort((a, b) => a.percentageCorrect - b.percentageCorrect)
      .slice(0, 5)
  );

  readonly displayedMessageAdherenceData = computed(() =>
    [...this.messageAdherenceData()]
      .sort((a, b) => {
        const notMentionedDiff =
          b.articulationProgress.NOT_MENTIONED.percentage -
          a.articulationProgress.NOT_MENTIONED.percentage;
        if (notMentionedDiff !== 0) return notMentionedDiff;
        return (
          b.articulationProgress.PARTIALLY_ARTICULATED.percentage -
          a.articulationProgress.PARTIALLY_ARTICULATED.percentage
        );
      })
      .slice(0, 5)
  );

  onViewAllObjectivesV2(): void {
    this.listModalRef?.open();
  }

  onViewAllObjectivesV1(): void {
    this.listModalRef?.open();
  }

  /**
   * Maps API response to segments format for qai-segmented-bar-graph (V1)
   * Order: success (FULLY_ARTICULATED), info (PARTIALLY_ARTICULATED), warning (NOT_MENTIONED)
   * Filters out segments with 0 percentage
   */
  mapToSegments(
    articulationProgress: IMessageAdherenceAverageScoresResponse['articulationProgress'],
  ): Array<{
    value: number;
    kind: 'success' | 'neutral' | 'info' | 'warning';
  }> {
    const segments = [
      {
        value: parseFloat(this.localeService.formatScoreValue(articulationProgress.FULLY_ARTICULATED.percentage.toString(), 1)),
        kind: 'success' as const,
      },
      {
        value: parseFloat(this.localeService.formatScoreValue(articulationProgress.PARTIALLY_ARTICULATED.percentage.toString(), 1)),
        kind: 'info' as const,
      },
      {
        value: parseFloat(this.localeService.formatScoreValue(articulationProgress.NOT_MENTIONED.percentage.toString(), 1)),
        kind: 'warning' as const,
      },
    ];

    // Filter out segments with 0 percentage
    return segments.filter((segment) => segment.value > 0);
  }

  /**
   * Generates aria label for accessibility (V1)
   */
  generateAriaLabel(
    articulationProgress: IMessageAdherenceAverageScoresResponse['articulationProgress'],
  ): string {
    return `${this.localeService.formatScoreValue(articulationProgress.FULLY_ARTICULATED.percentage.toString(), 1)}% fully, ${this.localeService.formatScoreValue(articulationProgress.PARTIALLY_ARTICULATED.percentage.toString(), 1)}% partially, ${this.localeService.formatScoreValue(articulationProgress.NOT_MENTIONED.percentage.toString(), 1)}% not mentioned`;
  }

  getKindByPercentage(percentage: number): QaiSegmentedBarGraphKind {
    return getKindByPercentage(percentage);
  }
}
